import { useCallback, useMemo, useState } from 'react'
import { type NextRouter, useRouter } from 'next/router'

import type { Category } from '@mc/api'
import { QUIZ_QUERY_PARAMS } from '@mc/find-my-classes'
import { TFunction } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'

export const useCategoriesStatic = () => {
  const { categories } = useStaticData<{ categories?: Category[] }>()
  return categories
}

export type Motivation = ReturnType<typeof useMotivations>[number]
export type MotivationsMap = Partial<Record<Motivation, Readonly<string[]>>>

const MOTIVATION_CATEGORY_MAP = {
  pro: ['Business & Entrepreneurship', 'Community & Government'],
  art: ['Acting & Performing Arts', 'Film & TV', 'Music', 'Writing'],
  health: ['Health & Wellness', 'Sports & Athletics'],
  science: ['Science & Technology'],
  chef: ['Food & Drink'],
  style: ['Art & Design'],
  other: ['Games & Digital Media', 'Outdoor Adventures & Events'],
} as const

const MOTIVATION_CATEGORY_MAP_ALT = {
  pro: ['Business & Entrepreneurship', 'Community & Government'],
  art: ['Acting & Performing Arts', 'Film & TV', 'Music', 'Writing'],
  health: ['Health & Wellness', 'Sports & Athletics'],
  science: ['Science & Technology'],
  chef: ['Food & Drink'],
  style: ['Art & Design'],
  gaming: ['Games & Digital Media'],
  outdoor: ['Outdoor Adventures & Events'],
} as const

export const useMotivations = (alt = false) => {
  const baseMap = useMotivationToCategoriesBaseMap(alt)
  return Object.keys(baseMap)
}

export const useOtherCategories = () => MOTIVATION_CATEGORY_MAP.other

const useMotivationToCategoriesBaseMap = (alt = false) =>
  alt ? MOTIVATION_CATEGORY_MAP_ALT : MOTIVATION_CATEGORY_MAP

export const useMotivationsCopyMap = (
  motivations: Readonly<Motivation[]>,
  t: TFunction<'@mc/next'>,
) => {
  const getMotivationCopy = (motivation: Motivation) =>
    t(`home.hero.palmMotivations.${motivation}`)

  return motivations.reduce(
    (acc, motivation) => {
      acc[motivation] = getMotivationCopy(motivation)
      return acc
    },
    {} as Record<Motivation, string>,
  )
}

export const useCategoriesIdToNameMap = () => {
  const categories = useCategoriesStatic()

  return useMemo(() => {
    const map = new Map<number, string>()

    if (!categories) return map
    categories.forEach(({ id, name }) => {
      if (id && name) {
        map.set(Number(id), name)
      }
    })
    return map
  }, [categories])
}

export const useCategoryNameToIdMap = () => {
  const idToCatName = useCategoriesIdToNameMap()
  return useMemo(
    () => new Map([...idToCatName.entries()].map(([k, v]) => [v, k])),
    [idToCatName],
  )
}

const useComputeOtherCategories = (
  motivationsMap: MotivationsMap,
): string[] => {
  const categories = useCategoriesStatic()
  const existing = useMemo(
    () => new Set(Object.values(motivationsMap).flat()),
    [motivationsMap],
  )
  return useMemo(
    () =>
      (categories || [])
        .filter((c) => c.name && !existing.has(c.name))
        .map((c) => c.name)
        .filter((name): name is string => typeof name === 'string'),
    [categories, existing],
  )
}

export const useMotivationToCategoriesMap = (alt = false): MotivationsMap => {
  const baseMotivationsMap: MotivationsMap =
    useMotivationToCategoriesBaseMap(alt)
  const other = useComputeOtherCategories(baseMotivationsMap)

  return useMemo(() => {
    const motivationsMap = { ...baseMotivationsMap }
    if (!motivationsMap.other && other.length && !alt) {
      motivationsMap.other = other
    }
    return motivationsMap
  }, [alt, baseMotivationsMap, other])
}

const getCategoriesCSV = (router: NextRouter) => {
  // prefer using window if possible
  if (typeof window !== 'undefined') {
    const qp = new URLSearchParams(window.location.search)
    const q = qp.get(QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES)
    if (typeof q === 'string') return q
  }
  if (router) {
    const q = router.query?.[QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES]
    if (typeof q === 'string') return q
  }
  return ''
}

const useSelectedCategoriesFromURL = (forcedCategories: number[] = []) => {
  const router = useRouter()
  const categoriesCSV = getCategoriesCSV(router)

  const categoriesIdMap = useCategoriesIdToNameMap()

  const newSelectedCategories = new Map<string, number>()

  categoriesCSV
    .split(',')
    .map(Number)
    .concat(forcedCategories)
    .forEach((categoryId) => {
      const categoryName = categoriesIdMap.get(categoryId)
      if (categoryName) {
        newSelectedCategories.set(categoryName, categoryId)
      }
    })

  return newSelectedCategories
}

export const useSelectedCategories = (forcedCategories?: number[]) => {
  const initFromUrl = useSelectedCategoriesFromURL(forcedCategories)
  const [selectedCategories, setSelectedCategories] = useState(initFromUrl)

  const categoriesIdMap = useCategoriesIdToNameMap()

  const toggle = useCallback(
    (catId: Category['id']) => {
      if (!catId) return
      const id = Number(catId)
      const categoryName = categoriesIdMap.get(id)
      setSelectedCategories((oldSelected) => {
        if (!categoryName) return oldSelected
        const selected = new Map(oldSelected)
        if (selected.has(categoryName)) {
          selected.delete(categoryName)
        } else {
          selected.set(categoryName, id)
        }
        return selected
      })
    },
    [categoriesIdMap],
  )

  const isSelected = (catId: Category['id']) => {
    if (!catId) return false
    const id = Number(catId)
    const categoryName = categoriesIdMap.get(id)
    if (!categoryName) return false
    return selectedCategories.has(categoryName)
  }

  const syncToUrl = () => {
    const qp = new URLSearchParams(window.location.search)
    if (selectedCategories.size === 0) {
      qp.delete(QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES)
    } else {
      const pval = [...selectedCategories.values()].join(',')
      qp.set(QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES, encodeURI(pval))
      // ensure that `?categories=` query param occurs before the skipQ1 (`?wq=t`) query param
      const skipQ1queryParamVal = qp.get(QUIZ_QUERY_PARAMS.SKIP_Q1)
      if (skipQ1queryParamVal) {
        qp.delete(QUIZ_QUERY_PARAMS.SKIP_Q1)
        qp.set(QUIZ_QUERY_PARAMS.SKIP_Q1, skipQ1queryParamVal)
      }
    }
    const hasQPs = [...qp.keys()].length
    window.history.replaceState(null, '', hasQPs ? `?${qp.toString()}` : '')
  }

  return {
    selectedCategories,
    setSelectedCategories,
    toggle,
    isSelected,
    syncToUrl,
  }
}
